import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Sparkles, Plus, Loader2, Play, Pause, Trash2, Eye, X, Clock, Layers, Check, ChevronRight, ChevronLeft, AlertTriangle, Image as ImageIcon, Send, CheckCircle2,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { PLATFORM_META, type Platform } from '../lib/platforms';

type Campaign = {
  id: string;
  name: string;
  productInfo: string;
  postsPerDay: number;
  slidesPerPost: number;
  timezone: string;
  status: 'active' | 'paused';
  accountCount: number;
  bestHours: number[];
  createdAt: string;
};

type ConnectedAccount = {
  id: string;
  platform: Platform;
  username: string | null;
  displayName: string | null;
  status: 'connected' | 'disconnected';
};

type Slide = { caption: string; imagePrompt: string; imageUrl?: string };

const fmtHour = (h: number) => `${String(h).padStart(2, '0')}:00`;

export function Autopost() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [previewFor, setPreviewFor] = useState<Campaign | null>(null);

  const campaignsQuery = useQuery({
    queryKey: ['autopost', 'campaigns'],
    queryFn: () => api<{ campaigns: Campaign[] }>('/api/admin/autopost/campaigns'),
    enabled: user?.role === 'admin',
  });

  const toggle = useMutation({
    mutationFn: (c: Campaign) =>
      api(`/api/admin/autopost/campaigns/${c.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: c.status === 'active' ? 'paused' : 'active' }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['autopost', 'campaigns'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/admin/autopost/campaigns/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['autopost', 'campaigns'] }),
  });

  // Admin-only page: bounce everyone else back to the dashboard home.
  if (user && user.role !== 'admin') return <Navigate to="/dashboard" replace />;

  const campaigns = campaignsQuery.data?.campaigns ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles size={22} className="text-brand-600" /> Autopost
          </h1>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            Campañas de creación y publicación automática de carruseles de slides. La IA genera el ángulo de
            venta, los textos y las imágenes, y las publica en las cuentas elegidas a las mejores horas.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-primary gap-2">
          <Plus size={16} /> Nueva campaña
        </button>
      </div>

      {campaignsQuery.isLoading ? (
        <div className="flex items-center gap-2 text-slate-400 py-10">
          <Loader2 size={16} className="animate-spin" /> Cargando campañas…
        </div>
      ) : campaigns.length === 0 ? (
        <EmptyState onCreate={() => setCreating(true)} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {campaigns.map((c) => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onToggle={() => toggle.mutate(c)}
              onDelete={() => {
                if (window.confirm(`¿Eliminar la campaña "${c.name}"? Esto no borra los posts ya publicados.`)) remove.mutate(c.id);
              }}
              onPreview={() => setPreviewFor(c)}
              busy={toggle.isPending || remove.isPending}
            />
          ))}
        </div>
      )}

      {creating && <CreateWizard onClose={() => setCreating(false)} onCreated={() => { setCreating(false); qc.invalidateQueries({ queryKey: ['autopost', 'campaigns'] }); }} />}
      {previewFor && <PreviewModal campaign={previewFor} onClose={() => setPreviewFor(null)} />}
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
      <div className="mx-auto h-12 w-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center">
        <Layers size={22} />
      </div>
      <h2 className="mt-4 text-lg font-semibold text-slate-800">Aún no tienes campañas</h2>
      <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
        Crea tu primera campaña: describe tu producto, elige cuántas veces al día publicar y en qué cuentas.
        El resto lo hace la IA.
      </p>
      <button onClick={onCreate} className="btn-primary gap-2 mt-5 mx-auto">
        <Plus size={16} /> Crear campaña
      </button>
    </div>
  );
}

function CampaignCard({
  campaign, onToggle, onDelete, onPreview, busy,
}: {
  campaign: Campaign;
  onToggle: () => void;
  onDelete: () => void;
  onPreview: () => void;
  busy: boolean;
}) {
  const active = campaign.status === 'active';
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5 flex flex-col">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-bold text-slate-900 leading-tight">{campaign.name}</h3>
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full border px-2.5 py-1 ${active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
          {active ? <Play size={11} /> : <Pause size={11} />} {active ? 'Activa' : 'Pausada'}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
        <Stat label="Publicaciones/día" value={String(campaign.postsPerDay)} />
        <Stat label="Slides por post" value={String(campaign.slidesPerPost)} />
        <Stat label="Cuentas" value={String(campaign.accountCount)} />
        <Stat label="Zona horaria" value={campaign.timezone.split('/')[1] ?? campaign.timezone} />
      </div>

      <div className="mt-3">
        <p className="text-xs font-medium text-slate-400 mb-1.5 flex items-center gap-1"><Clock size={12} /> Mejores horas</p>
        <div className="flex flex-wrap gap-1.5">
          {campaign.bestHours.map((h) => (
            <span key={h} className="text-xs font-medium rounded-md bg-brand-50 text-brand-700 px-2 py-0.5">{fmtHour(h)}</span>
          ))}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
        <button onClick={onPreview} className="btn-secondary gap-1.5 flex-1 justify-center text-sm">
          <Eye size={14} /> Preview
        </button>
        <button onClick={onToggle} disabled={busy} className="btn-secondary gap-1.5 text-sm disabled:opacity-50" title={active ? 'Pausar' : 'Reanudar'}>
          {active ? <Pause size={14} /> : <Play size={14} />}
        </button>
        <button onClick={onDelete} disabled={busy} className="btn-secondary gap-1.5 text-sm !text-red-600 hover:!bg-red-50 disabled:opacity-50" title="Eliminar">
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-[11px] text-slate-400 leading-none">{label}</p>
      <p className="text-sm font-semibold text-slate-800 mt-1">{value}</p>
    </div>
  );
}

/* ── Create wizard ──────────────────────────────────────── */
function CreateWizard({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [productInfo, setProductInfo] = useState('');
  const [postsPerDay, setPostsPerDay] = useState(2);
  const [slidesPerPost, setSlidesPerPost] = useState(2);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const accountsQuery = useQuery({
    queryKey: ['social', 'accounts'],
    queryFn: () => api<{ accounts: ConnectedAccount[] }>('/api/social/accounts'),
  });
  const accounts = (accountsQuery.data?.accounts ?? []).filter((a) => a.status === 'connected');

  const create = useMutation({
    mutationFn: () =>
      api('/api/admin/autopost/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          productInfo: productInfo.trim(),
          postsPerDay,
          slidesPerPost,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          accountIds,
        }),
      }),
    onSuccess: onCreated,
    onError: (e) => setError(e instanceof ApiError ? (e.message || 'No se pudo crear la campaña.') : 'No se pudo crear la campaña.'),
  });

  const canNext1 = name.trim().length > 0 && productInfo.trim().length >= 10;
  const canCreate = accountIds.length > 0;

  return (
    <Modal onClose={onClose} title="Nueva campaña de Autopost" wide>
      <Stepper step={step} />

      {step === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nombre de la campaña</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej. Zettapost — captación" className="input w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Información del producto</label>
            <p className="text-xs text-slate-400 mb-2">
              Describe qué vendes, todas las features, el tono y el público objetivo. Cuanto más detalle, mejores serán los carruseles.
            </p>
            <textarea
              value={productInfo}
              onChange={(e) => setProductInfo(e.target.value)}
              rows={9}
              placeholder="Ej. Zettapost es un SaaS para programar y publicar en 9 redes sociales desde un solo lugar. Features: calendario visual, cola inteligente, IA que escribe captions, analíticas unificadas, flujo de aprobación con cliente, integración con ChatGPT (MCP), equipos…"
              className="input w-full font-mono text-sm"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Publicaciones por día</label>
            <p className="text-xs text-slate-400 mb-3">Elegiremos automáticamente las mejores horas del día.</p>
            <NumberPicker value={postsPerDay} onChange={setPostsPerDay} min={1} max={6} />
            <BestHoursPreview n={postsPerDay} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Slides por publicación</label>
            <p className="text-xs text-slate-400 mb-3">Formato viral: una portada con gancho + una slide con la lista de valor.</p>
            <NumberPicker value={slidesPerPost} onChange={setSlidesPerPost} min={2} max={2} />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-slate-700">Cuentas donde se publicarán los carruseles</label>
          {accountsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-slate-400 py-6"><Loader2 size={16} className="animate-spin" /> Cargando cuentas…</div>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-slate-500 py-4">No tienes cuentas conectadas. Conéctalas en «Cuentas conectadas» primero.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {accounts.map((a) => {
                const meta = PLATFORM_META[a.platform];
                const Icon = meta.icon;
                const selected = accountIds.includes(a.id);
                return (
                  <button
                    key={a.id}
                    type="button"
                    onClick={() => setAccountIds((prev) => selected ? prev.filter((x) => x !== a.id) : [...prev, a.id])}
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition ${selected ? 'border-brand-500 bg-brand-50' : 'border-slate-200 hover:border-slate-300'}`}
                  >
                    <span className="h-9 w-9 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                      <Icon size={16} color={meta.color} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{a.displayName || a.username || meta.label}</p>
                      <p className="text-xs text-slate-400">{meta.label}</p>
                    </div>
                    {selected && <Check size={16} className="text-brand-600 shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <button onClick={() => (step === 1 ? onClose() : setStep(step - 1))} className="btn-secondary gap-1.5">
          {step === 1 ? 'Cancelar' : <><ChevronLeft size={15} /> Atrás</>}
        </button>
        {step < 3 ? (
          <button
            onClick={() => setStep(step + 1)}
            disabled={step === 1 && !canNext1}
            className="btn-primary gap-1.5 disabled:opacity-50"
          >
            Siguiente <ChevronRight size={15} />
          </button>
        ) : (
          <button onClick={() => create.mutate()} disabled={!canCreate || create.isPending} className="btn-primary gap-1.5 disabled:opacity-50">
            {create.isPending && <Loader2 size={15} className="animate-spin" />}
            Crear campaña
          </button>
        )}
      </div>
    </Modal>
  );
}

function Stepper({ step }: { step: number }) {
  const labels = ['Producto', 'Frecuencia', 'Cuentas'];
  return (
    <div className="flex items-center gap-2 mb-6">
      {labels.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const active = step === n;
        return (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${done ? 'bg-brand-600 text-white' : active ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-400'}`}>
              {done ? <Check size={14} /> : n}
            </div>
            <span className={`text-xs font-medium ${active ? 'text-slate-800' : 'text-slate-400'}`}>{label}</span>
            {n < 3 && <div className="h-px flex-1 bg-slate-200" />}
          </div>
        );
      })}
    </div>
  );
}

function NumberPicker({ value, onChange, min, max }: { value: number; onChange: (n: number) => void; min: number; max: number }) {
  const options = [];
  for (let i = min; i <= max; i++) options.push(i);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          className={`h-11 w-11 rounded-xl border text-sm font-semibold transition ${value === n ? 'border-brand-500 bg-brand-600 text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function BestHoursPreview({ n }: { n: number }) {
  const presets: Record<number, number[]> = {
    1: [10], 2: [10, 18], 3: [10, 15, 20], 4: [10, 15, 18, 23], 5: [9, 12, 15, 18, 22], 6: [9, 12, 15, 18, 21, 23],
  };
  const hours = presets[Math.max(1, Math.min(6, n))] ?? [10];
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-slate-400 mr-1">Horas:</span>
      {hours.map((h) => (
        <span key={h} className="text-xs font-medium rounded-md bg-brand-50 text-brand-700 px-2 py-0.5">{fmtHour(h)}</span>
      ))}
    </div>
  );
}

/* ── Preview modal ──────────────────────────────────────── */
function PreviewModal({ campaign, onClose }: { campaign: Campaign; onClose: () => void }) {
  const preview = useMutation({
    mutationFn: () => api<{ angle: string; postCaption: string; slides: Slide[] }>(`/api/admin/autopost/campaigns/${campaign.id}/preview`, { method: 'POST' }),
  });

  const publish = useMutation({
    mutationFn: () =>
      api<{ postId: string }>(`/api/admin/autopost/campaigns/${campaign.id}/publish-preview`, {
        method: 'POST',
        body: JSON.stringify({
          caption: preview.data!.postCaption,
          media: preview.data!.slides
            .filter((s) => s.imageUrl)
            .map((s) => ({ url: s.imageUrl!, type: 'image' })),
        }),
      }),
  });

  const data = preview.data;

  return (
    <Modal onClose={onClose} title={`Preview — ${campaign.name}`} wide>
      {!data && !preview.isPending && (
        <div className="text-center py-8">
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            Generaremos un carrusel de ejemplo (portada con gancho + slide de lista) con el ángulo, los textos
            y la imagen real. Puede tardar hasta ~1 minuto.
          </p>
          <button onClick={() => preview.mutate()} className="btn-primary gap-2 mt-5 mx-auto">
            <Sparkles size={16} /> Generar preview
          </button>
        </div>
      )}

      {preview.isPending && (
        <div className="flex flex-col items-center justify-center py-12 gap-3 text-slate-500">
          <Loader2 size={28} className="animate-spin text-brand-600" />
          <p className="text-sm">Generando ángulo, textos e imágenes…</p>
          <p className="text-xs text-slate-400">Esto puede tardar un poco (genera la foto de portada con IA).</p>
        </div>
      )}

      {preview.isError && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          {preview.error instanceof ApiError ? (preview.error.message || 'No se pudo generar el preview.') : 'No se pudo generar el preview.'}
        </div>
      )}

      {data && (
        <div className="space-y-5">
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Ángulo de venta</p>
            <p className="text-sm text-slate-700">{data.angle}</p>
          </div>
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-1">Texto de la publicación</p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{data.postCaption}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Slides ({data.slides.length})</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {data.slides.map((s, i) => (
                <div key={i} className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
                  {s.imageUrl ? (
                    <img src={s.imageUrl} alt={`Slide ${i + 1}`} className="w-full aspect-[4/5] object-cover" />
                  ) : (
                    <div className="w-full aspect-[4/5] flex items-center justify-center text-slate-300"><ImageIcon size={28} /></div>
                  )}
                  <p className="text-xs text-slate-500 p-2 line-clamp-2">{s.caption}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => preview.mutate()}
              disabled={preview.isPending || publish.isPending}
              className="btn-secondary gap-2 text-sm"
            >
              <Sparkles size={14} /> Regenerar
            </button>
            {publish.isSuccess ? (
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                <CheckCircle2 size={16} /> Publicación creada
              </span>
            ) : (
              <button
                onClick={() => publish.mutate()}
                disabled={publish.isPending}
                className="btn-primary gap-2 text-sm"
              >
                {publish.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Publicar ahora
              </button>
            )}
          </div>
          {publish.isError && (
            <p className="text-xs text-red-600 text-right">
              {publish.error instanceof ApiError ? (publish.error.message || 'No se pudo publicar.') : 'No se pudo publicar.'}
            </p>
          )}
          {publish.isSuccess && (
            <p className="text-xs text-slate-400 text-right">Se publicará en las cuentas de la campaña en breve.</p>
          )}
        </div>
      )}
    </Modal>
  );
}

/* ── Modal shell ────────────────────────────────────────── */
function Modal({ children, onClose, title, wide }: { children: React.ReactNode; onClose: () => void; title: string; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-6 overflow-y-auto bg-slate-900/40">
      <div className={`bg-white rounded-2xl shadow-xl w-full ${wide ? 'max-w-2xl' : 'max-w-md'} my-8`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
